/**
 * AIBriefingWizard Types
 *
 * Type definitions for the multi-step AI Briefing Wizard.
 * This wizard replaces the single-modal AIBriefingModal with a guided flow.
 */

// Wizard step identifiers
export type WizardStep = 'platform' | 'audience' | 'style' | 'images' | 'generating' | 'result';

// Step configuration for the step indicator
export interface WizardStepConfig {
  id: WizardStep;
  label: string;  // i18n key
  isConditional?: boolean;  // True for steps that may be skipped
}

// Form data structure (matches existing AIBriefingModal state)
export interface AIBriefingFormData {
  platform: string;
  objective: string;
  audience: string;
  context: string;
  contentFormat: 'text' | 'image_text' | 'video_text' | 'carousel_text';
  wantImageSuggestion: boolean;
  imageDescription: string;
  tone: string;
  formality: number;
  length: 'short' | 'medium' | 'long';
  language: string;
}

// Validation errors map
export type ValidationErrors = Partial<Record<keyof AIBriefingFormData, string>>;

// Props shared by all step components
export interface StepProps {
  formData: AIBriefingFormData;
  onFieldChange: <K extends keyof AIBriefingFormData>(field: K, value: AIBriefingFormData[K]) => void;
  errors?: ValidationErrors;
}

// Unsplash image type (from existing code)
export interface UnsplashImage {
  id: string;
  url: string;
  thumbnailUrl: string;
  photographer: string;
  photographerUrl: string;
  downloadUrl: string;  // Required for Unsplash API compliance
}

// AI message type (from existing code)
export interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Default form data
export const DEFAULT_FORM_DATA: AIBriefingFormData = {
  platform: '',
  objective: '',
  audience: '',
  context: '',
  contentFormat: 'text',
  wantImageSuggestion: false,
  imageDescription: '',
  tone: 'friendly_professional',
  formality: 5,
  length: 'medium',
  language: 'English',
};

// Step configuration (ordered)
export const WIZARD_STEPS: WizardStepConfig[] = [
  { id: 'platform', label: 'ai_wizard_step_platform' },
  { id: 'audience', label: 'ai_wizard_step_audience' },
  { id: 'style', label: 'ai_wizard_step_style' },
  { id: 'images', label: 'ai_wizard_step_images', isConditional: true },
];

// Helper to check if format includes images
export function formatIncludesImages(format: AIBriefingFormData['contentFormat']): boolean {
  return ['image_text', 'carousel_text'].includes(format);
}
